import { useContext, useCallback, useState, useEffect } from 'react';
import styles from './quiz-photo-upload.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle,
    faEllipsis,
    faImage,
} from '@fortawesome/pro-solid-svg-icons';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import classNames from 'classnames';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';

export const QuizPhotoUpload = () => {
    const {
        currentQuestion,
        handleSubmitAnswer,
        handleNavigateNextQuestion,
        state,
    } = useContext(QuizContext);
    const { answerList, questionImage, questionText, medication } =
        currentQuestion || {};
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [loading, setLoading] = state.loading;
    const [isError, setIsError] = useState(false);
    const [image, setImage] = useState<string | null>(null);

    // Handle file input
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files?.[0]) {
            const file = event.target.files[0];
            if (file.type.startsWith('image')) {
                handleSubmit(file);
                setImage(URL.createObjectURL(event.target.files[0]));
            } else {
                setIsError(true);
                setImage(null);
            }
        }
    };

    // Handle submitting
    const handleSubmit = useCallback(
        (file: File) => {
            if (currentQuestion.questionId) {
                setIsError(false);
                if (!continueDisabled) setContinueDisabled(true);
                const { answerId = '', answerText = '' } = answerList[0] || {};

                handleSubmitAnswer(
                    {
                        questionId: currentQuestion.questionId,
                        questionText: currentQuestion.questionText,
                        answerIds: [answerId],
                        answerTexts: [answerText],
                        uploadFile: file,
                        lastModifiedDate: new Date(file.lastModified),
                        medication: medication,
                    },
                    (r) => {
                        if (r) {
                            setContinueDisabled(false);
                        } else {
                            setIsError(true);
                            setImage(null);
                        }
                    },
                );
                setLoading(false);
            }
        },
        [
            answerList,
            continueDisabled,
            currentQuestion,
            handleSubmitAnswer,
            setContinueDisabled,
            medication,
        ],
    );

    // Handle continuing
    const handleContinue = useCallback(() => {
        if (!continueDisabled) {
            handleNavigateNextQuestion();
        }
    }, [continueDisabled, handleNavigateNextQuestion]);

    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleContinue();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleContinue]);

    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleContinue);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleContinue,
            );
    }, [handleContinue]);

    return (
        <div className={styles['quiz-photo-upload']}>
            <div className={styles['quiz-field-header']}>
                <h4 className={styles['title']}>{questionText}</h4>
                <picture>
                    <img
                        src={questionImage}
                        className="h-16 w-16"
                        alt={questionText}
                    />
                </picture>
            </div>
            <label
                className={classNames(styles['upload-container'], {
                    [styles['upload-container--error']]: isError,
                    [styles['upload-container--success']]:
                        image && !continueDisabled,
                })}
            >
                <input
                    data-testid="file-upload-input"
                    type="file"
                    onChange={handleInputChange}
                    disabled={loading}
                    accept="image/*"
                />
                {loading ? (
                    <div className={styles['loading-container']}>
                        <FontAwesomeIcon
                            icon={faEllipsis}
                            className="h-12 w-12 text-grey-300"
                        />
                        <span className="text-center">Uploading...</span>
                    </div>
                ) : image && !continueDisabled ? (
                    <div className={styles['success-container']}>
                        <img src={image} alt="Uploaded" />
                        <div className={styles['success-content']}>
                            <div className={styles['success-text']}>
                                Upload Complete
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    className="pl-2"
                                />
                            </div>
                            <span>Tap the image to upload a new one</span>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center space-y-3">
                        <FontAwesomeIcon
                            icon={faImage}
                            className="h-12 w-12 text-grey-300"
                        />
                        <span className="text-center">
                            Tap to upload an image from your device
                        </span>
                    </div>
                )}
            </label>
        </div>
    );
};
